import React from 'react';
import NotificationIcon from './NotificationIcon';
import NotificationActions from './NotificationActions';
import { T, RADIUS, QUOTE_FONT, BUBBLE_FONT } from './notificationModel';
import { LockIcon } from '../messages/SecureLockSheet';

/**
 * One card on the notifications page (handoff §1 "Feed column → Rows"): icon · type pill
 * (+ time-left pill) · title · body in one of three voices · actions · time + unread dot.
 *
 *   n        a decorated row (notificationModel.decorate)
 *   phone    < 640: the 48px icon column, 14px padding
 *   compact  the denser variant (48px column, 12/16 padding, 13px title, 17px quote)
 *   onOpen   (n) → the row was tapped: mark read + follow the deep link
 *   onRead   (id) → mark read without leaving
 */
export default function NotificationRow({ n, phone = false, compact = false, onOpen, onRead }) {
  const iconSize = compact ? 44 : 52;
  const cols = phone || compact ? '48px minmax(0,1fr) auto' : '58px minmax(0,1fr) auto';
  const pad = compact ? '12px 16px 12px 12px' : phone ? '14px' : '18px 20px 18px 16px';
  const titleFs = compact ? 13 : 14;
  const quoteFs = compact ? 17 : phone ? 18 : 22;

  return (
    <article
      data-testid="notification-row"
      data-unread={n.unread ? '' : undefined}
      onClick={() => onOpen?.(n)}
      style={{
        display: 'grid', gridTemplateColumns: cols, gap: phone ? 12 : 16, alignItems: 'start', padding: pad,
        borderRadius: RADIUS.row, background: n.unread ? T.white : T.readCard, cursor: 'pointer', transition: 'background-color .2s',
      }}
    >
      {/* 3px of room on the right and below so the badge can overhang the tile. */}
      <span style={{ display: 'flex', padding: '0 3px 3px 0' }}>
        <NotificationIcon type={n.type} family={n.t.family} size={iconSize} face={n.face} live={n.live} />
      </span>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {n.label && (
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', padding: '4px 9px', borderRadius: RADIUS.pill, background: n.pillBg, color: n.pillFg }}>
              {n.label}
            </span>
          )}
          {n.left && (
            <span data-testid="notification-left" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.12em', padding: '4px 9px', borderRadius: RADIUS.pill, background: T.live, color: '#fff' }}>
              {n.left}
            </span>
          )}
        </div>

        {n.isPortal && n.name ? (
          <span style={{ fontSize: titleFs, lineHeight: 1.35 }}><b>{n.name}</b> opened a portal</span>
        ) : (
          <span style={{ fontSize: titleFs, fontWeight: n.unread ? 700 : 500, lineHeight: 1.35, textWrap: 'pretty', display: 'flex', alignItems: 'center', gap: 6 }}>
            {n.secure && <LockIcon size={13} data-secure-glyph style={{ flexShrink: 0 }} />}
            {n.title}
          </span>
        )}

        {n.variant === 'quote' && (
          <p style={{ margin: '2px 0 0', fontFamily: QUOTE_FONT, fontStyle: 'italic', fontSize: quoteFs, lineHeight: 1.2, textWrap: 'pretty' }}>
            {'“'}{n.body}{'”'}
          </p>
        )}
        {n.variant === 'bubble' && (
          <span style={{ alignSelf: 'flex-start', maxWidth: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: RADIUS.bubble, background: T.off, fontFamily: BUBBLE_FONT, fontSize: 14, lineHeight: 1.4 }}>
            {n.body}
          </span>
        )}
        {n.variant === 'plain' && (
          <span style={{ fontSize: 12, color: T.grey600, lineHeight: 1.55, textWrap: 'pretty' }}>{n.body}</span>
        )}
        {n.hero && (
          <img src={n.hero} alt="" style={{ display: 'block', maxWidth: 320, width: '100%', borderRadius: 14, marginTop: 2 }} />
        )}

        <NotificationActions n={n} onRead={onRead} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, paddingTop: 2 }}>
        <span style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey400, whiteSpace: 'nowrap' }}>{n.ago}</span>
        {n.unread && <span aria-label="Unread" style={{ width: 9, height: 9, borderRadius: '50%', background: T.live }} />}
      </div>
    </article>
  );
}
